import { useEffect } from 'react';
import { googleReviews, practice } from '../data/site';
import { patientInfo } from '../data/content';
import { images } from '../data/images';
const TITLE = 'Dentist in Miami Lakes, FL | Lima Dental Group';

// Applied at module evaluation so the tab never flashes the host's title.
if (typeof document !== 'undefined') document.title = TITLE;
const DESCRIPTION = 'Lima Dental Group is a family-owned dental practice in Miami Lakes, Florida, with more than 35 years of experience. General, restorative, cosmetic, Invisalign and emergency dental care. Walk-ins welcome, new patients welcome, care in English and Spanish.';
export function SeoHead() {
  useEffect(() => {
    document.title = TITLE;
    document.documentElement.lang = 'en';
    const setMeta = (name: string, content: string) => {
      let tag = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };
    setMeta('description', DESCRIPTION);
    setMeta('theme-color', '#050505');
    setMeta('geo.placename', 'Miami Lakes, Florida');
    // Google Search Console property verification.
    setMeta('google-site-verification', 'R1kfaQ1cmglNugwRrHlJQ51feHEI0ttAoU2ujuvyj7c');

    // Link previews (messaging apps, social) read property="og:*" tags.
    const setProperty = (property: string, content: string) => {
      let tag = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };
    setProperty('og:type', 'website');
    setProperty('og:site_name', practice.name);
    setProperty('og:title', TITLE);
    setProperty('og:description', DESCRIPTION);
    setProperty('og:url', practice.url);
    setProperty('og:image', images.hero);
    setProperty('og:locale', 'en_US');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', TITLE);
    setMeta('twitter:description', DESCRIPTION);
    setMeta('twitter:image', images.hero);
    const dentist = {
      '@context': 'https://schema.org',
      '@type': 'Dentist',
      '@id': practice.url,
      name: practice.name,
      description: DESCRIPTION,
      url: practice.url,
      telephone: practice.phone,
      email: practice.email,
      address: {
        '@type': 'PostalAddress',
        streetAddress: practice.address.street,
        addressLocality: practice.address.city,
        addressRegion: practice.address.region,
        postalCode: practice.address.postalCode,
        addressCountry: 'US'
      },
      founder: {
        '@type': 'Person',
        name: 'Dr. Julio C. Lima, DDS'
      },
      employee: {
        '@type': 'Person',
        name: 'Dr. Julio C. Lima, DDS',
        jobTitle: 'Dentist'
      },
      foundingDate: practice.since,
      hasMap: practice.mapsUrl,
      potentialAction: {
        '@type': 'ReserveAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: practice.bookingUrl
        }
      },
      areaServed: ['Miami Lakes', 'Miami-Dade County', 'South Florida'],
      availableLanguage: ['English', 'Spanish'],
      knowsLanguage: ['en', 'es'],
      currenciesAccepted: 'USD',
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: googleReviews.rating,
        reviewCount: googleReviews.count,
        bestRating: '5'
      },
      openingHoursSpecification: [{
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '08:00',
        closes: '17:00'
      }],
      makesOffer: ['Dental cleanings', 'Dental exams', 'Dental implants', 'Crowns and bridges', 'Root canal therapy', 'Invisalign', 'Veneers', 'Teeth whitening', 'Emergency dental care'].map(name => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name
        }
      }))
    };
    const faq = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: patientInfo.map(item => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.a
        }
      }))
    };
    const write = (id: string, data: unknown) => {
      let script = document.getElementById(id) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    };
    write('ldg-schema-dentist', dentist);
    write('ldg-schema-faq', faq);

    /**
     * The preview host can overwrite document.title with the version name at
     * any point after mount — sometimes by replacing the <title> element
     * entirely — so watch the whole head and keep re-asserting the practice
     * title.
     */
    const enforce = () => {
      if (document.title !== TITLE) document.title = TITLE;
    };
    const observer = new MutationObserver(enforce);
    observer.observe(document.head, {
      childList: true,
      characterData: true,
      subtree: true
    });
    const interval = window.setInterval(enforce, 400);
    const stop = window.setTimeout(() => window.clearInterval(interval), 10000);
    return () => {
      observer.disconnect();
      window.clearInterval(interval);
      window.clearTimeout(stop);
    };
  }, []);
  return null;
}